/**
 * Wire values, not display order. The backend sends these numbers on `SignalRecommenderFileDto.status`,
 * and `modules/enrichment/store/type.ts` declares the same three against the same `/sales-api` job
 * family — so they are a contract to preserve exactly, not an internal numbering.
 */
export const signalRecommenderFileStatusMap = {
    succeeded: 1,
    inProgress: 2,
    failed: 3,
} as const;

export type SignalRecommenderFileStatus =
    (typeof signalRecommenderFileStatusMap)[keyof typeof signalRecommenderFileStatusMap];

export type SignalRecommenderFile = {
    readonly id: number;
    readonly fileName: string;
    readonly created: string;
    readonly companiesCount: number;
    readonly signalsFoundCount: number;
    readonly status: SignalRecommenderFileStatus;
    readonly isDownloaded: boolean;
};

/** Everything the list endpoint answers at once: the enrichment history, and whether a new run is allowed. */
export type SignalRecommenderHistory = {
    readonly files: readonly SignalRecommenderFile[];
    readonly nextUploadAllowedAt?: string | null;
};

/**
 * Pre-flight result for the picked CSV. `companiesCount` counts distinct companies rather than rows, so
 * the preview shown before upload measures the same thing the server reports back after it.
 */
export type DealSignalCsvInvalidReason = "invalidFormat" | "noDataRows" | "tooManyCompanies";

export type DealSignalCsvValidation =
    | { readonly isValid: true; readonly companiesCount: number; readonly reason?: undefined }
    | { readonly isValid: false; readonly reason: DealSignalCsvInvalidReason; readonly companiesCount: number };

export type SignalEnrichmentIntentFiltersPayload = {
    readonly parentCategories: readonly string[];
    readonly categories: readonly string[];
    readonly topics: readonly string[];
};

export type SignalEnrichmentTechnologyFiltersPayload = {
    readonly categories: readonly string[];
    readonly subCategories: readonly string[];
    readonly technologies: readonly string[];
};

/**
 * At least one side is always present: "no filters at all" is expressed by omitting `signalFilters`
 * entirely, so a payload that narrows nothing cannot be constructed.
 */
export type SignalEnrichmentFiltersPayload =
    | {
          readonly intent: SignalEnrichmentIntentFiltersPayload;
          readonly technology: SignalEnrichmentTechnologyFiltersPayload | null;
      }
    | { readonly intent: null; readonly technology: SignalEnrichmentTechnologyFiltersPayload };

export type DealSignalUploadInput = {
    readonly file: File;
    readonly signalFilters?: SignalEnrichmentFiltersPayload;
};
